import type { FileForProcessing, IOrder, ISamplePool } from 'Model/index.js'
import { isSampleReadyForSubmission } from 'Model/index.js'
import React, { useEffect, useState } from 'react'
import type { Column, ColumnKey } from './SampleDataColumns.js'
import { headerOf, headersMetadata, headersSampleOrNC } from './HeaderConfig.js'
import { SampleTableRow } from './SampleTableRow.js'
import type { ISampleMetaData } from '../../../Model/Sample/ISampleMetaData.js'
import { useDispatch, useStore } from 'react-redux'
import Actions from '../../../Flux/index.js'
import { orderSlice } from '../../../Flux/Slice/Order/OrderSlice.js'
import { GroupingPanel } from '../SampleGrouping/UI/GroupingPanel.js'
import { addMessage } from '../../../Flux/Slice/Application/ApplicationSlice.js'
import { errorMessage } from '../../../Model/Message/IMessage.js'
import { mapToBatches } from '../../Page/HomePage/Modals/MapToBatches.js'
import { generateSampleNames } from './SampleTableUtils.js'
import type { VisualizationSampleContainer } from '../SampleGrouping/VisualizationSampleContainer.js'
import { STATIC_METADATA_FIELDS } from '../SampleGrouping/MetadataFields.ts'
import { useConfirm } from '../ConfirmProvider.js'
import { metadataDeleteChoiceMessage } from './MetadataDeleteMessage.js'
import type { State } from '../../../Flux/Slice/index.js'
import { getSampleMetaDatas } from '../../../Flux/Selector/Order/GetSampleMetaDatas/GetSampleMetaDataSelector.js'
import { getSamplePools } from '../../../Flux/Selector/Order/GetSamplePool/GetSamplePoolSelector.js'
import { currentMetadatas, currentPoolItem } from '../../../Flux/Selector/Order/CurrentOrderState.js'

interface SampleTableProps {
  order: IOrder
  dataset: ISamplePool
  isMetadataTab: boolean
  /** Order operations: sample type, primers, deletion, bulk selection, the actions column. */
  editable: boolean
  /**
   * Metadata edits, which the server allows for any org on the order for its whole life
   * (`SampleMetadataController.updateBatch` checks only `existsByIdAndOrganization`) — not only
   * before submission and not only for the lab. Kept apart from `editable` so the table agrees with
   * the CSV importer and the edit grid, which have always allowed it.
   */
  metadataEditable: boolean
  isDone: boolean
  allPools: ISamplePool[]
}

export type AdjustedSample = FileForProcessing & {
  forwardFileNameStripped: string
  reverseFileNameStripped: string
}

export function SampleTable(props: SampleTableProps) {
  const [hideCompleted, setHideCompleted] = useState(false)
  const [hideMerged, setHideMerged] = useState(false)
  const [isSelectSamples, setIsSelectSamples] = useState(false)
  const [headers, setHeaders] = useState<Column[]>([])
  const [selectionState, setSelectionState] = useState<Record<string, boolean>>({})
  const [filesExpanded, setFilesExpanded] = useState(false)

  const [allSamples, setAllSamples] = useState<VisualizationSampleContainer[]>([])
  const [sampleToVersion, setSampleToVersion] = useState<Record<string, string>>({})
  const [filteredSamples, setFilteredSamples] = useState<FileForProcessing[]>([])
  const [finalFilteredSamples, setFinalFilteredSamples] = useState<FileForProcessing[]>([])
  const [customShown, setCustomShown] = useState(false)
  const [filterBoxText, setFilterBoxText] = useState<string>('')

  useEffect(() => {
    setAllSamples(
      [...props.dataset.samples, ...props.dataset.negativeControls].map((x) => ({
        ...x,
        baseOrder: props.order,
        order: props.order
      }))
    )
    setFilteredSamples([...props.dataset.samples, ...props.dataset.negativeControls])
    setSampleToVersion(
      [...props.dataset.samples, ...props.dataset.negativeControls].reduce((acc, x) => ({ ...acc, [x.id]: x.version }), {})
    )
  }, [props.dataset.samples, props.dataset.negativeControls])

  useEffect(() => {
    if (filterBoxText.length < 2) {
      setFinalFilteredSamples(filteredSamples)
    } else {
      setFinalFilteredSamples(searchEveryWhere(filteredSamples, filterBoxText))
    }
  }, [filteredSamples, filterBoxText])

  const handleHideCompleted = (v: React.MouseEvent<HTMLInputElement>) => {
    const target = v.target as HTMLInputElement
    setHideCompleted(target.checked)
  }

  const handleHideMerged = (v: React.MouseEvent<HTMLInputElement>) => {
    const target = v.target as HTMLInputElement
    setHideMerged(target.checked)
  }

  const determineHeaders: (props: SampleTableProps) => Column[] = (props: SampleTableProps) => {
    let base = headersSampleOrNC
    if (props.isMetadataTab) {
      base = headersMetadata
    } else if (props.isDone) {
      base = [headerOf('otuCount'), ...base]
    } else {
      base = [headerOf('pipelineStatus'), ...base]
    }
    base = [...base, ...determineMetadataHeaders(props)]
    if (props.editable) {
      return [...base, headerOf('actions')]
    }
    return base
  }

  const options: { key: ColumnKey; field: (m?: ISampleMetaData) => unknown }[] = [
    { key: 'sampleCoordinates', field: (m) => m?.sampleCoordinates },
    { key: 'samplingDate', field: (m) => m?.samplingDate },
    { key: 'samplingStart', field: (m) => m?.samplingStart },
    { key: 'samplingEnd', field: (m) => m?.samplingEnd },
    { key: 'matrixType', field: (m) => m?.matrixType },
    { key: 'extractionKit', field: (m) => m?.extractionKit },
    { key: 'trapType', field: (m) => m?.trapType },
    { key: 'collectionType', field: (m) => m?.collectionType },
    { key: 'sequencer', field: (m) => m?.sequencer },
    { key: 'sequencingChemistry', field: (m) => m?.sequencingChemistry },
    { key: 'sequencingDepth', field: (m) => m?.sequencingDepth },
    { key: 'localityType', field: (m) => m?.localityType },
    { key: 'habitatType', field: (m) => m?.habitatType },
    { key: 'custom', field: (m) => m?.customAttributes }
  ]

  const determineMetadataHeaders: (props: SampleTableProps) => Column[] = (props: SampleTableProps) => {
    const allElements = [...props.dataset.samples, ...props.dataset.negativeControls]
    return options.filter((x) => allElements.some((element) => x.field(element.sampleMetadata))).map((x) => headerOf(x.key))
  }

  useEffect(() => {
    setIsSelectSamples(false)
    setSelectionState({})
  }, [props])

  useEffect(() => {
    if (!isSelectSamples) {
      setSelectionState({})
    }
  }, [isSelectSamples])

  useEffect(() => {
    setFilesExpanded(false)
  }, [props.dataset.id])

  useEffect(() => {
    if (isSelectSamples) {
      setHeaders([...[{ label: '', type: 'select' as ColumnKey }], ...determineHeaders(props)])
    } else {
      setHeaders(determineHeaders(props))
    }
  }, [props, isSelectSamples])
  const allFiles = [...props.dataset.samples, ...props.dataset.negativeControls].flatMap((x) => [
    x.forwardFile?.fileName ?? '',
    x.reverseFile?.fileName ?? ''
  ])
  const filesPrefix = getCommonPrefix(allFiles)
  const filesSuffix = getCommonSuffix(allFiles)

  const dispatch = useDispatch()
  const { confirm, choose } = useConfirm()
  const store = useStore<typeof State>()

  const selectVisible = () => {
    setSelectionState(finalFilteredSamples.reduce((acc, sample) => ({ ...acc, [sample.id]: true }), {} as Record<string, boolean>))
  }

  /** Metadata rows backing the currently-selected table rows (metadata tab only). */
  const selectedMetadatas = (): ISampleMetaData[] =>
    [...props.dataset.samples, ...props.dataset.negativeControls]
      .filter((s) => selectionState[s.id])
      .map((s) => s.sampleMetadata)
      .filter((m): m is ISampleMetaData => !!m)

  const deleteSelectedMetadata = async () => {
    const metas = selectedMetadatas()
    if (metas.length === 0) return
    await confirm(
      {
        title: `Delete ${metas.length} metadata ${metas.length === 1 ? 'entry' : 'entries'}?`,
        message: 'This permanently removes the selected metadata entries. This cannot be undone.',
        confirmLabel: 'Delete'
      },
      () =>
        metas.forEach((m) =>
          dispatch(Actions[orderSlice.name].deleteMetadataItemRequest({ orderId: props.order.id, metadataId: m.id }))
        )
    )
  }

  const unlinkSelectedMetadata = () => {
    // Unlink only applies to merged metadata (sampleId set); unmerged rows are silently skipped.
    const metas = selectedMetadatas().filter((m) => m.sampleId)
    if (metas.length === 0) {
      dispatch(addMessage(errorMessage('None of the selected metadata entries are merged to a sample, so there is nothing to unlink.')))
      return
    }
    dispatch(Actions[orderSlice.name].unlinkMetadatasRequest({ orderId: props.order.id, metadatas: metas }))
  }

  /**
   * Batch-delete the selected samples. If any of them have metadata merged to them, first ask once
   * what should happen to that metadata (delete or unlink), mirroring the pool-delete flow, then
   * delete every selected sample. Samples without metadata are deleted straight away.
   */
  const deleteSelectedSamples = () => {
    const items = [...props.dataset.samples, ...props.dataset.negativeControls]
    const selectedIds = Object.entries(selectionState)
      .filter(([, selected]) => selected)
      .map(([id]) => id)
    const removeSamples = () =>
      selectedIds.forEach((id) =>
        dispatch(
          Actions[orderSlice.name].deletePoolItemRequest({
            orderId: props.dataset.orderId,
            poolId: props.dataset.id,
            /* may run after a confirm dialog closes: the version it was opened with can be stale */
            version: currentPoolItem(getSamplePools(store.getState()), id)?.version ?? sampleToVersion[id],
            itemId: id
          })
        )
      )
    const metas = selectedIds
      .map((id) => items.find((s) => s.id === id)?.sampleMetadata)
      .filter((m): m is ISampleMetaData => !!m && !!m.sampleId)
    if (metas.length === 0) {
      removeSamples()
      return
    }
    choose(
      {
        title: `Do you really want to delete ${selectedIds.length} selected sample(s)?`,
        message: metadataDeleteChoiceMessage(selectedIds.length, metas.length)
      },
      [
        {
          label: 'Delete Metadata',
          testId: 'batch-delete-sample-metadata-choice',
          className: 'btn-primary',
          value: 1,
          operation: () => {
            metas.forEach((m) =>
              dispatch(Actions[orderSlice.name].deleteMetadataItemRequest({ orderId: props.dataset.orderId, metadataId: m.id }))
            )
          }
        },
        {
          label: 'Unlink Metadata',
          testId: 'batch-unlink-sample-metadata-choice',
          className: 'btn-primary',
          value: 2,
          operation: () => {
            dispatch(
              Actions[orderSlice.name].unlinkMetadatasRequest({
                orderId: props.dataset.orderId,
                metadatas: currentMetadatas(getSampleMetaDatas(store.getState()), metas)
              })
            )
          }
        }
      ],
      async () => {
        removeSamples()
      }
    )
  }

  return (
    <div>
      {!props.isMetadataTab ? (
        /* normal pool */
        <div className={'flex items-center justify-between'}>
          <div className="flex items-center gap-2">
            <label className="label cursor-pointer border-neutral-400 border-2 rounded-md space-x-1">
              <span className="label-text font-light"> hide completed Samples</span>
              <input type="checkbox" className="checkbox-sm" onClick={handleHideCompleted} checked={hideCompleted} onChange={() => {}} />
            </label>
            <button data-test-id="batch-select-samples" className="btn btn-sm" onClick={() => setIsSelectSamples((prev) => !prev)}>
              {isSelectSamples ? 'Unselect' : 'Batch Select Samples'}
            </button>
            {isSelectSamples && (
              <button className={'btn btn-sm'} onClick={() => selectVisible()}>
                Select Visible
              </button>
            )}
          </div>
          {props.editable && (
            <div className="flex items-center gap-2">
              {isSelectSamples && Object.values(selectionState).filter((x) => x).length > 0 && (
                <>
                  <button data-test-id={'batch-delete-samples'} className={'btn btn-sm'} onClick={deleteSelectedSamples}>
                    Delete Selected
                  </button>
                  <button
                    data-test-id={'batch-convert-samples'}
                    className={'btn btn-sm'}
                    onClick={() => {
                      // Toggles the type of each selected item (SAMPLE <-> NC). Conversion is now
                      // reversible — the epic flips the type per item and metadata is preserved — so
                      // no confirmation is needed.
                      const items = [...props.dataset.samples, ...props.dataset.negativeControls]
                      Object.entries(selectionState)
                        .filter((x) => x[1])
                        .forEach((x) => {
                          const item = items.find((s) => s.id === x[0])
                          if (!item) {
                            dispatch(addMessage(errorMessage(`Could not find selected item with id ${x[0]}`)))
                          } else {
                            dispatch(
                              Actions[orderSlice.name].convertPoolItemRequest({
                                orderId: props.dataset.orderId,
                                poolId: props.dataset.id,
                                item
                              })
                            )
                          }
                        })
                    }}
                  >
                    Toggle Sample / NC
                  </button>
                  <button
                    data-test-id={'batch-generate-names'}
                    className={'btn btn-sm'}
                    onClick={async () => {
                      mapToBatches(generateSampleNames(props.dataset.samples)).forEach((x) => {
                        dispatch(Actions[orderSlice.name].batchUpdateSamples(x))
                      })
                    }}
                  >
                    Generate Sample Names
                  </button>
                  <select
                    data-test-id={'batch-move-samples'}
                    className="select select-sm w-full max-w-xs"
                    defaultValue=""
                    onChange={(e) => {
                      const selectedId = e.target.value
                      if (selectedId) {
                        // S2-945: only move items still present in this pool. A stale selection
                        // (e.g. a sample deleted after "Select Visible") would otherwise be sent
                        // and rejected with a 404, rolling back the whole batch.
                        const presentIds = new Set(
                          [...props.dataset.samples, ...props.dataset.negativeControls].map((s) => s.id)
                        )
                        const items = Object.entries(selectionState)
                          .filter(([id, selected]) => selected && presentIds.has(id))
                          .map(([id]) => id)
                        if (items.length === 0) {
                          dispatch(
                            addMessage(
                              errorMessage(
                                'None of the selected samples are still available to move. Please refresh and try again.'
                              )
                            )
                          )
                          return
                        }
                        dispatch(
                          Actions[orderSlice.name].movePoolItemsRequest({
                            orderId: props.dataset.orderId,
                            sourcePoolId: props.dataset.id,
                            targetPoolId: selectedId,
                            items
                          })
                        )
                      }
                    }}
                  >
                    <option disabled value="">
                      Move to Pool..
                    </option>
                    {props.allPools.map((pool, index) => (
                      <option key={pool.id} value={pool.id} disabled={pool.id === props.dataset.id} data-test-id={'move-to-pool-' + index}>
                        Pool {index}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
          )}
        </div>
      ) : (
        /* viewing a fake pool containing only metadata */
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <label className="label cursor-pointer border-neutral-400 border-2 rounded-md space-x-1">
              <span className="label-text font-light"> hide merged Metadata</span>
              <input type="checkbox" className="checkbox-sm" onClick={handleHideMerged} checked={hideMerged} onChange={() => {}} />
            </label>
            {props.editable && (
              <button data-test-id="batch-select-metadata" className="btn btn-sm" onClick={() => setIsSelectSamples((prev) => !prev)}>
                {isSelectSamples ? 'Unselect' : 'Batch Select Metadata'}
              </button>
            )}
            {props.editable && isSelectSamples && (
              <button className={'btn btn-sm'} onClick={() => selectVisible()}>
                Select Visible
              </button>
            )}
          </div>
          {props.editable && isSelectSamples && Object.values(selectionState).filter((x) => x).length > 0 && (
            <div className="flex items-center gap-2">
              <button data-test-id="batch-unlink-metadata" className="btn btn-sm" onClick={unlinkSelectedMetadata}>
                Unlink Selected
              </button>
              <button data-test-id="batch-delete-metadata" className="btn btn-sm btn-error" onClick={deleteSelectedMetadata}>
                Delete Selected
              </button>
            </div>
          )}
        </div>
      )}
      <div>
        <input
          className={'input input-sm mt-2 w-full'}
          type="text"
          placeholder="Search everywhere..."
          onChange={(e) => setFilterBoxText(e.target.value)}
        />
      </div>
      <div className={'mt-2'}>
        <GroupingPanel
          samples={allSamples}
          mode={'only-filter'}
          onBucketsChange={(buckets, pipeline) => {
            console.info('buckets changed', buckets, pipeline)
            setFilteredSamples(buckets[0].samples)
          }}
        />
      </div>
      <div className="overflow-x-auto">
        <div className="max-h-[80vh] overflow-y-auto">
          <table className="table table-zebra table-pin-rows table-xs">
            <thead className="table-header-group">
              <tr>
                {headers.map((h) => (
                  <th key={`header-${h.type}-${h.label}`}>{h.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="[&>tr:nth-child(odd)]:bg-[oklch(92.4169%_.00108_197.137559/1)]">
              {finalFilteredSamples
                .filter((x) => x.itemType === 'SAMPLE')
                .filter((sample) => (hideCompleted ? !isSampleReadyForSubmission(sample) : true))
                .filter((sample) => (hideMerged ? !sample.sampleMetadata?.sampleId : true))
                .map((sample) => ({
                  ...sample,
                  forwardFileNameStripped: sliceString(sample.forwardFile?.fileName, filesPrefix, filesSuffix),
                  reverseFileNameStripped: sliceString(sample.reverseFile?.fileName, filesPrefix, filesSuffix)
                }))
                .map((sample) => {
                  return (
                    <SampleTableRow
                      key={sample.id}
                      orderId={props.dataset.orderId}
                      poolId={props.dataset.id}
                      editable={props.editable}
                      metadataEditable={props.metadataEditable}
                      sampleOrNC={sample}
                      columns={headers.map((x) => x.type)}
                      isSelected={selectionState[sample.id] ?? false}
                      onSelect={(isSelected) => setSelectionState((prev) => ({ ...prev, [sample.id]: isSelected }))}
                      filesExpanded={filesExpanded}
                      setFilesExpanded={setFilesExpanded}
                      customState={customShown}
                      setCustomState={setCustomShown}
                      isMetadataTab={props.isMetadataTab}
                    />
                  )
                })}
              {finalFilteredSamples
                .filter((x) => x.itemType === 'NEGATIVE_CONTROL')
                .filter((negativeControl) => (hideCompleted ? !isSampleReadyForSubmission(negativeControl) : true))
                .map((negativeControl) => ({
                  ...negativeControl,
                  forwardFileNameStripped: sliceString(negativeControl.forwardFile?.fileName, filesPrefix, filesSuffix),
                  reverseFileNameStripped: sliceString(negativeControl.reverseFile?.fileName, filesPrefix, filesSuffix)
                }))
                .map((negativeControl) => {
                  return (
                    <SampleTableRow
                      key={negativeControl.id}
                      orderId={props.dataset.orderId}
                      poolId={props.dataset.id}
                      editable={props.editable}
                      metadataEditable={props.metadataEditable}
                      sampleOrNC={negativeControl}
                      columns={headers.map((x) => x.type)}
                      isSelected={selectionState[negativeControl.id] ?? false}
                      onSelect={(isSelected) => setSelectionState((prev) => ({ ...prev, [negativeControl.id]: isSelected }))}
                      filesExpanded={filesExpanded}
                      setFilesExpanded={setFilesExpanded}
                      customState={customShown}
                      setCustomState={setCustomShown}
                    />
                  )
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function sliceString(input: string | undefined, commonPrefix: string, commonSuffix: string): string {
  return (input ?? '').slice(commonPrefix.length, (input ?? '').length - commonSuffix.length)
}

function getCommonPrefix(strings: string[]): string {
  if (strings.length === 0) return ''
  let prefix = strings[0]
  for (const str of strings) {
    let i = 0
    while (i < prefix.length && prefix[i] === str[i]) i++
    prefix = prefix.slice(0, i)
  }
  return prefix
}

function getCommonSuffix(strings: string[]): string {
  if (strings.length === 0) return ''
  let suffix = strings[0]
  for (const str of strings) {
    let i = 0
    while (i < suffix.length && suffix[suffix.length - 1 - i] === str[str.length - 1 - i]) {
      i++
    }
    suffix = suffix.slice(suffix.length - i)
  }
  return suffix
}

function searchEveryWhere(samples: FileForProcessing[], text: string): FileForProcessing[] {
  const lowercase = text.toLowerCase()
  return samples.filter((sample) =>
    Object.values(STATIC_METADATA_FIELDS).find((field) => field.getter(sample)?.toString()?.toLowerCase()?.includes(lowercase) ?? false)
  )
}
